import {
  Documentation,
  PutServiceDocRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { Button } from "@/shared/components/ui/button";
import { useQpmCalcServiceDocumentationPutMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import { useCallback, useEffect, useState } from "react";
import { useToast } from "@/shared/hooks/use-toast";
import { parseRTKQueryError } from "@/shared/components/errors/parse-r-t-k-query-error";
import { CalculationServiceType } from "@/services/backend/qpm/calculations/types/calculation-enum";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";

export type DocumentationComponentProps = {
  calculationId: string;
  serviceTypeKey: CalculationServiceType;
  documentations: Record<string, Documentation>;
  selectedDocumentationList: string[];
};

export function DocumentationComponent({
  calculationId,
  serviceTypeKey,
  documentations,
  selectedDocumentationList,
}: DocumentationComponentProps) {
  const { toast } = useToast();

  // Local state for immediate UI updates
  const [selectedKeys, setSelectedKeys] = useState<string[]>(
    selectedDocumentationList,
  );

  const [request, setRequest] = useState<PutServiceDocRequest>({
    calculationId,
    serviceTypeKey,
    keys: selectedDocumentationList,
  });

  const [setDocumentation, { isLoading, error, isSuccess, reset }] =
    useQpmCalcServiceDocumentationPutMutation();

  useEffect(() => {
    setSelectedKeys(selectedDocumentationList);
  }, [selectedDocumentationList]);

  const handleClick = useCallback(
    (val: string) => {
      let newKeys: string[];
      if (selectedKeys.some((key) => key === val)) {
        newKeys = selectedKeys.filter((key) => key !== val);
      } else {
        newKeys = [...selectedKeys, val];
      }

      // Update local state immediately for UI
      setSelectedKeys(newKeys);

      const req: PutServiceDocRequest = {
        calculationId,
        serviceTypeKey,
        keys: newKeys,
      };
      setRequest(req);
    },
    [calculationId, selectedKeys, serviceTypeKey],
  );

  useEffect(() => {
    if (error) {
      toast({ ...parseRTKQueryError(error) });
      reset();
    }
  }, [error, reset, toast]);

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setDocumentation,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  return (
    <div className="grid justify-between gap-2 md:grid-cols-2 xl:grid-cols-3">
      {Object.values(documentations).map((doc: Documentation) => (
        <div key={doc.id} className="flex items-center justify-start gap-1 p-1">
          <Checkbox
            checked={selectedKeys.some((key) => key === doc.key)}
            onClick={() => handleClick(doc.key)}
            disabled={isLoading}
          />
          <Button
            className="hover:text-primary bg-transparent p-1 text-base text-black hover:bg-transparent"
            onClick={() => handleClick(doc.key)}
            disabled={isLoading}
            variant={null}
          >
            {doc.name} ({(doc.duration / 60_000_000_000).toFixed(2)} min)
          </Button>
        </div>
      ))}
    </div>
  );
}
